import { useEffect, useState } from "react";
import { getCart, removeFromCart, clearCart, updateCartQuantity } from "../../utils/CartStorage";
import { Cart as CartType, Order, Item } from "../../entity/Entity";
import { AddOrder, GetItemByID } from "../../api/requests/Api";
import "./Cart.css";

type ItemPreview = {
    title: string;
    image?: string;
};

const Cart = () => {
    const [cart, setCart] = useState<CartType[]>([]);
    const [itemData, setItemData] = useState<Record<string, ItemPreview>>({});
    const [phone, setPhone] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string | null>(null);

    useEffect(() => {
        const storedCart = getCart();
        setCart(storedCart);

        const uniqueItemIds = new Set<string>();
        storedCart.forEach((c) => uniqueItemIds.add(c.item_id));

        uniqueItemIds.forEach((id) => resolveItemData(id));
        // eslint-disable-next-line
    }, []);

    const resolveItemData = async (itemId: string) => {
        if (itemData[itemId]) return;

        try {
            const data = await GetItemByID(itemId);

            if (data.length > 0) {
                const item: Item = data[0];

                setItemData((prev) => ({
                    ...prev,
                    [itemId]: {
                        title: item.title,
                        image: item.images_link?.[0],
                    },
                }));
            } else {
                setItemData((prev) => ({
                    ...prev,
                    [itemId]: {
                        title: "Unknown item",
                    },
                }));
            }
        } catch {
            setItemData((prev) => ({
                ...prev,
                [itemId]: {
                    title: "Unknown item",
                },
            }));
        }
    };

    const handleRemove = (index: number) => {
        removeFromCart(index);
        const updatedCart = getCart();
        setCart(updatedCart);
    };

    const handleSubmitOrder = async () => {
        if (!phone || !email) {
            setMessage("Please enter phone number and email");
            return;
        }

        if (cart.length === 0) {
            setMessage("Cart is empty");
            return;
        }

        const order: Order = {
            _id: "",
            status: "New",
            cart: cart,
            client: {
                phone_number: Number(phone),
                email: email,
                name: name,
            },
        };

        try {
            setLoading(true);
            await AddOrder(order as any);
            clearCart();
            setCart([]);
            setMessage("Order successfully created");
        } catch {
            setMessage("Failed to submit order");
        } finally {
            setLoading(false);
        }
    };

    const handleIncreaseQty = (index: number) => {
        const item = cart[index];
        updateCartQuantity(index, item.quantity + 1);
        setCart(getCart());
    };

    const handleDecreaseQty = (index: number) => {
        const item = cart[index];
        if (item.quantity <= 1) return;
        updateCartQuantity(index, item.quantity - 1);
        setCart(getCart());
    };


    return (
        <div className="cart-page">
            <h1 className="cart-title">Корзина</h1>

            {cart.length === 0 && (
                <div className="cart-empty">Корзина пуста</div>
            )}

            {cart.map((item, index) => {
                const preview = itemData[item.item_id];

                return (
                    <div key={index} className="cart-item">
                        {preview?.image && (
                            <img
                                src={preview.image}
                                alt={preview.title}
                                className="cart-item-image"
                            />
                        )}

                        <div className="cart-item-info">
                            <div className="cart-item-title">
                                {preview?.title || "Загрузка..."}
                            </div>

                            <div>
                                <strong>Выбранные характеристики:</strong>{" "}
                                {item.changeable_characteristics.join(", ")}
                            </div>

                            <div className="cart-qty">
                                <button onClick={() => handleDecreaseQty(index)}>-</button>
                                <span>{item.quantity}</span>
                                <button onClick={() => handleIncreaseQty(index)}>+</button>
                            </div>
                        </div>

                        <button
                            className="cart-remove-btn"
                            onClick={() => handleRemove(index)}
                        >
                            Удалить
                        </button>
                    </div>
                );
            })}

            {cart.length > 0 && (
                <div className="cart-form">
                    <h2>Информация для связи</h2>

                    <div className="cart-field">
                        <label>Имя</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Анна"
                        />
                    </div>

                    <div className="cart-field">
                        <label>Телефон</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+79999999999"
                        />
                    </div>

                    <div className="cart-field">
                        <label>Почта</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="example@mail.com"
                        />
                    </div>

                    {message && <div className="cart-message">{message}</div>}

                    <button
                        className="cart-submit-btn"
                        onClick={handleSubmitOrder}
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : "Place Order"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default Cart;
